// Instagram / WhatsApp / Facebook vb. uygulama-içi (in-app) tarayıcılar
// localStorage'ı geçici/izole tutar → Supabase persistSession çalışmaz →
// kullanıcı sürekli login'e atılır ("oturum kapanıyor" şikâyeti).
// Bu bileşen UA'dan in-app tarayıcıyı algılar ve gerçek tarayıcıda açması için uyarır.

import { useState } from 'react';
import { AlertTriangle, X, Copy, Check } from 'lucide-react';

// UA → uygulama adı (bilinen in-app webview imzaları)
function detectInAppBrowser(ua: string): string | null {
  const rules: [RegExp, string][] = [
    [/Instagram/i, 'Instagram'],
    [/FBAN|FBAV|FB_IAB|FBIOS|FB4A/i, 'Facebook'],
    [/WhatsApp/i, 'WhatsApp'],
    [/\bLine\//i, 'LINE'],
    [/Twitter|TwitterAndroid/i, 'X (Twitter)'],
    [/TikTok|musical_ly|BytedanceWebview|trill_/i, 'TikTok'],
    [/Snapchat/i, 'Snapchat'],
    [/LinkedInApp/i, 'LinkedIn'],
    [/KAKAOTALK/i, 'KakaoTalk'],
  ];
  for (const [re, name] of rules) if (re.test(ua)) return name;
  return null;
}

type Platform = 'ios' | 'android' | 'other';
function detectPlatform(ua: string): Platform {
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

export default function InAppBrowserWarning() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const app = detectInAppBrowser(ua);
  const platform = detectPlatform(ua);

  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);

  // In-app tarayıcı değilse (normal Chrome/Safari) hiçbir şey gösterme.
  if (!app || dismissed) return null;

  const howTo =
    platform === 'ios'
      ? 'Sağ alttaki/üstteki ••• (paylaş) simgesine dokun → "Safari\'de Aç".'
      : platform === 'android'
        ? 'Sağ üstteki ⋮ menüsüne dokun → "Tarayıcıda aç" / "Chrome\'da aç".'
        : 'Sayfa menüsünden "Tarayıcıda aç" seçeneğini kullan.';

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard izni yoksa sessizce geç — kullanıcı menüden açabilir
    }
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4">
      <div className="mx-auto max-w-lg bg-amber-500 text-amber-950 rounded-xl shadow-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sm">
              {app} tarayıcısındasın — oturumun sürekli kapanabilir
            </p>
            <p className="text-xs mt-1 leading-relaxed">
              Bu sayfayı uygulama içi tarayıcıda açtığın için giriş bilgilerin kaydedilmez.
              Lütfen <strong>Chrome veya Safari</strong>'de aç: {howTo}
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <button
                onClick={() => void copyLink()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-950 text-amber-50 rounded-lg text-xs font-medium hover:bg-amber-900"
              >
                {copied ? <Check size={13} /> : <Copy size={13} />}
                {copied ? 'Kopyalandı' : 'Linki kopyala'}
              </button>
              <span className="text-[11px] opacity-80">
                Tarayıcıda açtıktan sonra ana ekranına ekleyebilirsin.
              </span>
            </div>
          </div>
          <button
            onClick={() => setDismissed(true)}
            aria-label="Uyarıyı kapat"
            className="shrink-0 p-1 rounded hover:bg-amber-600/40"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
